import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'

import { TutorAction, Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useDisciplineStore } from '@entities/customer'
import { AppRoute } from '@shared/config'
import { resolveUrl } from '@shared/lib/browser'

import { useLessonRoomStore } from '../store'

interface TerminalRouteContext {
  kid: string
  isMath: boolean
  isLastLesson: boolean
}

export const getTerminalRoute = (booking: Booking | null, context: TerminalRouteContext): string | null => {
  if (!booking) return null

  const action = booking.tutor_action
  const hasAction = (key: TutorAction): boolean => action?.[key] === true

  const homeUrl = resolveUrl(context.isMath ? AppRoute.HomeMath : AppRoute.HomeEla, { kid: context.kid })
  const padUrl = (): string | null => {
    const url = resolveUrl(context.isMath ? AppRoute.PadMath : AppRoute.PadEla, { kid: context.kid })
    return url ? `${url}?notify_type=lesson_finished` : null
  }

  // A completed lesson: the last one upsells (PAD), otherwise paid → feedback, demo/free → dashboard.
  if (hasAction(TutorAction.LessonFinished)) {
    if (context.isLastLesson) {
      return padUrl()
    }
    return booking.type === BookingType.Paid
      ? resolveUrl(AppRoute.Feedback, { booking_id: booking.id })
      : homeUrl
  }

  // A no-show or canceled lesson never happened — send the customer straight to the
  // dashboard, never to feedback (there is nothing to rate). Deliberate divergence from
  // main-app's Booking.vue, which routes paid no-show/canceled through the feedback link.
  if (hasAction(TutorAction.NoShow) || hasAction(TutorAction.CanceledBad)) {
    return homeUrl
  }

  if (hasAction(TutorAction.ShowOffers)) {
    return padUrl()
  }

  if (booking.is_finished) {
    return homeUrl
  }

  return null
}

interface UseLessonRoomTerminalOptions {
  onBeforeRedirect?: () => void | Promise<void>
}

export const useLessonRoomTerminal = (options: UseLessonRoomTerminalOptions = {}): void => {
  const disciplineStore = useDisciplineStore()
  const lessonRoomStore = useLessonRoomStore()

  const route = useRoute()

  let handled = false

  watch(() => lessonRoomStore.getBooking, async (booking) => {
    if (handled) return

    const url = getTerminalRoute(booking, {
      kid: String(route.params.kid ?? ''),
      isMath: disciplineStore.isMath,
      isLastLesson: lessonRoomStore.isLastLesson,
    })

    if (!url) return

    handled = true

    await options.onBeforeRedirect?.()

    void navigateTo(url)
  }, { immediate: true })
}
